<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ConversationAiUsageAPI from 'dashboard/api/saas/conversationAiUsage';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();

const usage = ref(null);
const isLoading = ref(false);

const formatTokens = count => {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toString();
};

const fetchUsage = async () => {
  isLoading.value = true;
  try {
    const { data } = await ConversationAiUsageAPI.getUsage(
      props.conversationId
    );
    usage.value = data;
  } catch {
    usage.value = null;
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.conversationId, fetchUsage, { immediate: true });
</script>

<template>
  <div class="py-2">
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-3 text-n-slate-11"
    >
      <div class="i-lucide-loader-2 size-4 animate-spin" />
    </div>
    <div
      v-else-if="!usage || usage.request_count === 0"
      class="px-3 py-2 text-xs text-n-slate-11"
    >
      {{ t('CONVERSATION_SIDEBAR.AI_USAGE.EMPTY') }}
    </div>
    <div v-else class="flex flex-col gap-2 px-3">
      <div class="grid grid-cols-2 gap-2">
        <div class="flex flex-col gap-0.5 rounded-lg bg-n-alpha-2 px-3 py-2">
          <span class="text-[10px] font-medium uppercase text-n-slate-10">
            {{ t('CONVERSATION_SIDEBAR.AI_USAGE.TOTAL_TOKENS') }}
          </span>
          <span class="text-sm font-semibold text-n-slate-12">
            {{ formatTokens(usage.total_tokens) }}
          </span>
        </div>
        <div class="flex flex-col gap-0.5 rounded-lg bg-n-alpha-2 px-3 py-2">
          <span class="text-[10px] font-medium uppercase text-n-slate-10">
            {{ t('CONVERSATION_SIDEBAR.AI_USAGE.REQUESTS') }}
          </span>
          <span class="text-sm font-semibold text-n-slate-12">
            {{ usage.request_count }}
          </span>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-2">
        <div class="flex flex-col gap-0.5 rounded-lg bg-n-alpha-2 px-3 py-2">
          <span class="text-[10px] font-medium uppercase text-n-slate-10">
            {{ t('CONVERSATION_SIDEBAR.AI_USAGE.INPUT') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ formatTokens(usage.total_input) }}
          </span>
        </div>
        <div class="flex flex-col gap-0.5 rounded-lg bg-n-alpha-2 px-3 py-2">
          <span class="text-[10px] font-medium uppercase text-n-slate-10">
            {{ t('CONVERSATION_SIDEBAR.AI_USAGE.OUTPUT') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ formatTokens(usage.total_output) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
